/**
 * Shared TypeScript interfaces for EventManagement components
 */

import type { RowSelectionState } from '@tanstack/react-table';
import type { Salesperson } from '@api/orders';
import type { FulfilmentType } from '@api/events';
import type { CurrentUser } from '@/react/lib/capabilities';
import type { EventChip } from '@components/common/eventChips';
import type { FilterCategory } from '@components/filters';

/** Event tab filter values */
export const EVENT_TAB_VALUES = ['all', 'today', 'tomorrow'] as const;
export type EventTab = (typeof EVENT_TAB_VALUES)[number];

/** Tab configuration */
export interface EventTabConfig {
  key: EventTab;
  label: string;
}

/** Salesperson option */
export const EVENT_FORMAT_VALUES = ['buffet', 'mini_buffet', 'gathering_box'] as const;
export const EVENT_STATUS_FILTER_VALUES = ['draft', 'published'] as const;

export interface EventSalesperson {
  id: number;
  name: string;
}

/** Props for EventsTabs */
export interface EventsTabsProps {
  currentTab: EventTab;
  onTabChange: (tab: EventTab) => void;
  disabled?: boolean;
  className?: string;
}

/** Props for EventsToolbar */
export interface EventsToolbarProps {
  categories: FilterCategory[];
  onNewOrder: () => void;
  className?: string;
}

/** Generate mutation mode for the EventsSelectionDock bottom dock. */
export type GenerateBarMode = 'loading' | 'queued' | 'error';

/** Props for AllEventsPage */
export interface AllEventsPageProps {
  currentTab?: EventTab;
  totalCount?: number;
  events?: EventListItem[];
  salespersons: EventSalesperson[];
  selectedSalespersonIds?: string[];
  onTabChange: (tab: EventTab) => void;
  onSalespersonChange: (ids: string[]) => void;
  selectedFormats?: string[];
  onFormatsChange: (values: string[]) => void;
  selectedOrderStatuses?: string[];
  onOrderStatusesChange: (values: string[]) => void;
  selectedEventStatuses?: string[];
  onEventStatusesChange: (values: string[]) => void;
  onNewOrder: () => void;
  selectedDate?: Date;
  onDateChange?: (date: Date | undefined) => void;
  /** Opens the mobile date filter sheet (mobile-only "Select date" trigger row). */
  onOpenDateFilter: () => void;
  isLoading?: boolean;
  /** Background refetch state; dims the mobile card list. */
  isFetching?: boolean;
  onEventIdClick?: (orderId: number) => void;
  /** Controlled row selection, keyed by event id. */
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
  /** Called when a row Generate option is chosen, with the event id and resolved document types. */
  onGenerateEvent?: (eventId: number, documentTypes: string[]) => void;
  /** Pagination for the mobile card list pager; the desktop table pages via DataTableFooter. */
  pagination?: EventsCardListPagination;
  /** Signed-in user — gates the row-level Generate action (event_orders.generate). */
  currentUser?: CurrentUser;
}

/** Props for EventsMobileHeader */
export interface EventsMobileHeaderProps {
  /** Formatted event count for the title badge */
  count: string;
  /** Current search input value */
  searchValue: string;
  /** Callback when the search input changes (cleared on search close) */
  onSearchChange: (value: string) => void;
  /** Opens the app-shell mobile navigation drawer */
  onOpenNavigation?: () => void;
  /** Additional CSS classes */
  className?: string;
}

/** Props for EventsMobileFooter */
export interface EventsMobileFooterProps {
  /** Callback when the salesperson filter trigger is tapped */
  onOpenSalespersonFilter: () => void;
  /** Additional CSS classes */
  className?: string;
}

/** Props for EventsDateFilterSheet section component */
export interface EventsDateFilterSheetProps {
  /** Whether the sheet is open */
  open: boolean;
  /** Callback when the sheet open state changes */
  onOpenChange: (open: boolean) => void;
  /** Currently selected date */
  selected?: Date;
  /** Called with the draft date when Confirm is pressed; the sheet closes itself. */
  onSelect: (date: Date | undefined) => void;
}

/** Pagination state for the mobile events card list pager */
export interface EventsCardListPagination {
  /** Current page (1-indexed) */
  page: number;
  /** Total number of pages */
  totalPages: number;
  /** Callback when the page changes */
  onPageChange: (page: number) => void;
}

/** Props for EventsCardList section component (mobile rendering of the events table) */
export interface EventsCardListProps {
  /** Events to display */
  events: EventListItem[];
  /** Loading state (first load) */
  isLoading?: boolean;
  /** Fetching state (background refetch) */
  isFetching?: boolean;
  /** Event ID click callback (opens the order detail sheet); fires with the parent order's id */
  onEventIdClick?: (orderId: number) => void;
  /** Pager rendered after the cards when there is more than one page */
  pagination?: EventsCardListPagination;
  /** Additional CSS classes */
  className?: string;
}

/** Props for EventManagement wrapper */
export interface EventManagementProps {
  totalCount?: number;
  // Widened to the full `Salesperson` (incl. avatar_url) so the same value can
  // feed both the salesperson filter and the reused Create order modal.
  salespersons?: Salesperson[];
  currentUser?: CurrentUser;
}

/** Single specialist assigned to an event */
export interface EventSpecialist {
  name: string;
  phone?: string;
  label?: string;
}

/** Event row in the events list data table */
export interface EventListItem {
  id: string;
  /** Displayed event identifier — the stored `event_id` (e.g. "EVT-38E093"), which the events search bar matches. */
  eventIdentifier: string;
  /** Numeric primary key of the parent sales order; null when the event has no order. */
  orderId: number | null;
  salesOrderNumber: string;
  /** e.g. "Buffet", "Mini Buffet", "High Tea Buffet" */
  eventType: string;
  /** Normalized event type from the API, e.g. "buffet", "mini_buffet". Drives document availability. */
  eventTypeKey: string;
  productLine: string | null;
  /** Raw FulfilmentEvent status, e.g. "confirmed", "draft", "cancelled". Drives the Status column and publishability. */
  eventStatus: string;
  /** Product form, e.g. "buffet", "gathering_box"; null when the event has none. */
  form: string | null;
  guestCount: number;
  lineCount?: number;
  wayCount?: number;
  /** ISO date string */
  eventDate: string;
  /** e.g. "28 Nov 2025 (Fri)" */
  eventDateFormatted: string;
  pickupTime: string;
  serviceTime: string;
  /** Papercut-aligned occasion tokens, e.g. weddingEvent */
  tags: string[];
  /** Any closed operating hour overlaps the event's date — renders the Kitchen closed chip. Optional because demo/static fixtures omit it. */
  kitchenClosed?: boolean;
  /** security_clearance requirement toggle is active — renders the Security clearance chip. Optional because demo/static fixtures omit it. */
  securityClearance?: boolean;
  /** ISO pre-setup datetime; renders the Pre-setup chip when present. Optional because demo/static fixtures omit it. */
  preSetupDateTime?: string | null;
  /**
   * Chip descriptors derived from the same source as the order-detail event
   * card (common/eventChips.ts#deriveEventChips), so a chip added there
   * appears identically on both surfaces. Optional because demo/static
   * fixtures omit it.
   */
  chips?: EventChip[];
  /**
   * How the customer receives the food. Drives the delivery-details label:
   * `self_pickup` events read "Self-pickup" regardless of any stored address.
   * Optional because demo/static fixtures omit it (defaults to delivery-style).
   */
  fulfilmentType?: FulfilmentType;
  /** Delivery address */
  deliveryAddress?: string;
  /** A `000000` placeholder reddens the delivery address. Optional: static fixtures omit it. */
  postalCode?: string | null;
  /** Contact name */
  contactName: string;
  contactPhone: string;
  /** Organisation name (optional) */
  organisationName?: string;
  /** Salesperson */
  salesperson: {
    name: string;
    avatarUrl?: string;
  };
  /** Specialists list */
  specialists: EventSpecialist[];
}
